import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Attach Bearer token from localStorage to every request
axios.interceptors.request.use(config => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

const AuthContext = createContext(null);

// Global 401 interceptor - clear session on auth expiry
let _setUser = null;
axios.interceptors.response.use(
  res => res,
  err => {
    if (
      err.response?.status === 401 &&
      !err.config?.url?.includes("/auth/me") &&
      !err.config?.url?.includes("/auth/login") &&
      !err.config?.url?.includes("/auth/register")
    ) {
      localStorage.removeItem("access_token");
      if (_setUser) _setUser(null);
    }
    return Promise.reject(err);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const authSeq = useRef(0);

  useEffect(() => { _setUser = setUser; return () => { _setUser = null; }; }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) { setUser(null); return; }
    const seq = ++authSeq.current;
    try {
      const { data } = await axios.get(`${API}/api/auth/me`);
      if (authSeq.current === seq) setUser(data);
    } catch {
      if (authSeq.current === seq) {
        localStorage.removeItem("access_token");
        setUser(null);
      }
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    authSeq.current++;
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    authSeq.current++;
    const { data } = await axios.post(`${API}/api/auth/register`, { email, password, name });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    authSeq.current++;
    localStorage.removeItem("access_token");
    try { await axios.post(`${API}/api/auth/logout`); } catch {}
    setUser(null);
  };

  const updateUser = (updated) => setUser(updated);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function formatApiError(error) {
  if (!error) return "Something went wrong";
  const detail = error.response?.data?.detail;
  if (!detail) return error.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(", ");
  return String(detail);
}
